// 113 — UCI Adulto (Fase 4, Tandas 5a+5b+8a, ver PLAN.md "FASE 4 — spec
// completa" → "113 — Bitácora UCI Adulto"). Migrado del motor genérico
// compartido `cuidadoIntensivo.tsx` (Encounter+Observation+Condition
// mínimo, ~8 campos) a un componente propio — el footer real del
// formulario confirma "SNS-MSP/HCU-form.113/2021" (misma familia MSP que
// 051), así que usa el mismo header (`EncabezadoMspCampos`/
// `useDatosEncabezadoMsp`) y modo PDF 'msp'.
//
// Cierra Bloques A (header MSP) + B (antropometría) + C completo (datos de
// bitácora, diagnósticos de ingreso, constantes vitales + gráfico
// superpuesto de 4 series, ingresos, egresos, reflejos miotáticos,
// escalas, gases, laboratorio, balance hídrico, personal de enfermería) +
// D-J (ventilación mecánica, procedimientos, oxigenación, solicitud de
// exámenes, cuidados generales, interconsultas, informe de enfermería —
// Tanda 8a, K deferido/superseded, ver `bloques113Datos.ts`).
//
// FHIR: Encounter class=ACUTE + extensión de nivel de cuidado intensivo
// (mecanismo ya existente, preservado de `cuidadoIntensivo.tsx`), hasta 5
// Condition (uno por diagnóstico de ingreso con texto — el papel imprime 5
// filas, mismo mecanismo que "motivo de ingreso" tenía para 1 sola), y
// Talla/Peso real crean Observation reusando los LOINC ya definidos en
// `vitalSigns.ts` (mismo signo vital, sin código nuevo). El resto de este
// formulario es `Par[]` de respaldo + el diagrama vectorial del gráfico de
// constantes — ver `bloques113Datos.ts`.
import { useMedplum } from '@medplum/react-hooks';
import type { Observation } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloques113 } from './Bloques113';
import { ProfesionalPorTurno } from './ProfesionalPorTurno';
import { CAMPOS_PROFESIONAL_113 } from './profesionalPorTurnoDatos';
import { diagramaGraficoConstantesUciDeEstado, estadoInicialBitacora113, paresDeBitacora113 } from './bloques113Datos';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { EXT_NIVEL_CUIDADO_INTENSIVO } from './cuidadoIntensivo';
import { signoVitalPorClave, VITAL_SIGNS_CATEGORY } from './vitalSigns';

const CODIGO = '113';
const NIVEL_CUIDADO_INTENSIVO = 'UCI Adulto';

export function Formulario113({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.113');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { servicio: NIVEL_CUIDADO_INTENSIVO });

  const [bitacora, setBitacora] = useState(() => estadoInicialBitacora113());

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const lote = new LoteTransaccional();
      const pares: Par[] = paresDeBitacora113(t, bitacora);
      const diagramasGraficoConstantesUci = [diagramaGraficoConstantesUciDeEstado(bitacora.graficoConstantes)];
      const ahora = new Date().toISOString();

      if (encounter) {
        const actual = await medplum.readReference(encounter);
        const otrasExtensiones = (actual.extension ?? []).filter((e) => e.url !== EXT_NIVEL_CUIDADO_INTENSIVO);
        lote.actualizarEncounterAbierto({
          ...actual,
          class: { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'ACUTE' },
          extension: [...otrasExtensiones, { url: EXT_NIVEL_CUIDADO_INTENSIVO, valueString: NIVEL_CUIDADO_INTENSIVO }],
        });
      }

      const diagnosticosConTexto = bitacora.diagnosticos.filter((d) => d.texto.trim() !== '');
      for (const d of diagnosticosConTexto) lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(d), recordedDate: ahora });

      const observacionesAntropometria: Observation[] = [];
      const talla = bitacora.antropometria.talla.trim() === '' ? undefined : Number(bitacora.antropometria.talla);
      if (talla !== undefined && !Number.isNaN(talla)) {
        const signo = signoVitalPorClave('talla');
        observacionesAntropometria.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: signo.loinc, display: signo.etiqueta }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            valueQuantity: { value: talla, unit: signo.unidad, system: 'http://unitsofmeasure.org', code: signo.unidadUcum },
          }),
        );
      }
      const pesoReal = bitacora.antropometria.pesoReal.trim() === '' ? undefined : Number(bitacora.antropometria.pesoReal);
      if (pesoReal !== undefined && !Number.isNaN(pesoReal)) {
        const signo = signoVitalPorClave('peso');
        observacionesAntropometria.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: signo.loinc, display: signo.etiqueta }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            valueQuantity: { value: pesoReal, unit: signo.unidad, system: 'http://unitsofmeasure.org', code: signo.unidadUcum },
          }),
        );
      }

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp, undefined, undefined, undefined, undefined, undefined, diagramasGraficoConstantesUci);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloques113
        antropometria={bitacora.antropometria}
        onChangeAntropometria={(v) => setBitacora({ ...bitacora, antropometria: v })}
        datosBitacora={bitacora.datosBitacora}
        onChangeDatosBitacora={(v) => setBitacora({ ...bitacora, datosBitacora: v })}
        diagnosticos={bitacora.diagnosticos}
        onChangeDiagnosticos={(v) => setBitacora({ ...bitacora, diagnosticos: v })}
        constantesVitales={bitacora.constantesVitales}
        onChangeConstantesVitales={(v) => setBitacora({ ...bitacora, constantesVitales: v })}
        graficoConstantes={bitacora.graficoConstantes}
        onChangeGraficoConstantes={(v) => setBitacora({ ...bitacora, graficoConstantes: v })}
        ingresos={bitacora.ingresos}
        onChangeIngresos={(v) => setBitacora({ ...bitacora, ingresos: v })}
        egresos={bitacora.egresos}
        onChangeEgresos={(v) => setBitacora({ ...bitacora, egresos: v })}
        reflejos={bitacora.reflejos}
        onChangeReflejos={(v) => setBitacora({ ...bitacora, reflejos: v })}
        escalas={bitacora.escalas}
        onChangeEscalas={(v) => setBitacora({ ...bitacora, escalas: v })}
        gases={bitacora.gases}
        onChangeGases={(v) => setBitacora({ ...bitacora, gases: v })}
        laboratorio={bitacora.laboratorio}
        onChangeLaboratorio={(v) => setBitacora({ ...bitacora, laboratorio: v })}
        balanceHidrico={bitacora.balanceHidrico}
        onChangeBalanceHidrico={(v) => setBitacora({ ...bitacora, balanceHidrico: v })}
        personalEnfermeria={bitacora.personalEnfermeria}
        onChangePersonalEnfermeria={(v) => setBitacora({ ...bitacora, personalEnfermeria: v })}
        ventilacion={bitacora.ventilacion}
        onChangeVentilacion={(v) => setBitacora({ ...bitacora, ventilacion: v })}
        procedimientos={bitacora.procedimientos}
        onChangeProcedimientos={(v) => setBitacora({ ...bitacora, procedimientos: v })}
        oxigenacion={bitacora.oxigenacion}
        onChangeOxigenacion={(v) => setBitacora({ ...bitacora, oxigenacion: v })}
        solicitudExamenes={bitacora.solicitudExamenes}
        onChangeSolicitudExamenes={(v) => setBitacora({ ...bitacora, solicitudExamenes: v })}
        cuidadosGenerales={bitacora.cuidadosGenerales}
        onChangeCuidadosGenerales={(v) => setBitacora({ ...bitacora, cuidadosGenerales: v })}
        interconsultas={bitacora.interconsultas}
        onChangeInterconsultas={(v) => setBitacora({ ...bitacora, interconsultas: v })}
        informeEnfermeria={bitacora.informeEnfermeria}
        onChangeInformeEnfermeria={(v) => setBitacora({ ...bitacora, informeEnfermeria: v })}
        grupos={bitacora.grupos}
        onChangeGrupos={(v) => setBitacora({ ...bitacora, grupos: v })}
        procedimientosOtros={bitacora.procedimientosOtros}
        onChangeProcedimientosOtros={(v) => setBitacora({ ...bitacora, procedimientosOtros: v })}
      />

      <ProfesionalPorTurno
        prefijoI18n="formularios.f113.profesionalTurnos"
        prefijoId="f113-prof"
        campos={CAMPOS_PROFESIONAL_113}
        estado={bitacora.profesionalTurnos}
        onChange={(v) => setBitacora({ ...bitacora, profesionalTurnos: v })}
      />

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
